import React, { useState } from "react";
import {
  Button,
  CircularProgress,
  Alert,
  Box,
  Typography,
  Chip,
} from "@mui/material";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { RootState, useAppDispatch } from "../../store";
import { sendMessageToAgent } from "../../store/userSlice";
import { v4 as uuid } from "uuid";
import type { SubmitResponse, SkillResult } from "../../pages/SkillGapAssessment/types";
import axiosClient from "../../api/AxiosClient";
import styles from "./chatwindow.module.css";

function deriveStrengths(skills: SkillResult[]): SkillResult[] {
  const exceeds = skills
    .filter((s) => s.severity === "exceeds_target")
    .sort((a, b) => b.current_score - a.current_score);

  if (exceeds.length >= 3) return exceeds.slice(0, 3);

  const meets = skills
    .filter((s) => s.severity === "meets_target")
    .sort((a, b) => b.current_score - a.current_score);

  return [...exceeds, ...meets].slice(0, 3);
}

function deriveWeaknesses(skills: SkillResult[]): SkillResult[] {
  return skills
    .filter((s) => s.gap > 0)
    .sort((a, b) => b.gap - a.gap)
    .slice(0, 3);
}

export default function LearningPathButton(): JSX.Element {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [assessmentData, setAssessmentData] = useState<SubmitResponse | null>(
    null,
  );
  const [noDataAvailable, setNoDataAvailable] = useState(false);

  // Get current agent and session info from Redux
  const currentAgent = useSelector(
    (state: RootState) => state.agents?.currentAgent,
  );
  const params = new URLSearchParams(location.search);
  const sessionId = params.get("sessionId");
  const agentLoading = useSelector((state: RootState) => state.user.loading);

  const handleGenerateLearningPath = async () => {
    setLoading(true);
    setError(null);
    setNoDataAvailable(false);

    try {
      const response = await axiosClient.get<SubmitResponse>(
        "/skill-gap-information",
      );

      // Check if response has valid data
      const hasValidData =
        response.data &&
        ((response.data.skills && response.data.skills.length > 0) ||
          (response.data.result?.data?.skills &&
            response.data.result.data.skills.length > 0));

      if (!hasValidData) {
        setNoDataAvailable(true);
        setAssessmentData(null);
      } else {
        setAssessmentData(response.data);
      }
    } catch (err: any) {
      console.error("Error fetching skill gap information:", err);

      // Check if it's a 404 or no data error
      if (
        err.response?.status === 404 ||
        err.response?.data?.message?.includes("not found")
      ) {
        setNoDataAvailable(true);
      } else {
        setError(
          err.response?.data?.message ||
            "Failed to fetch skill gap assessment data. Please try again.",
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setAssessmentData(null);
    setError(null);
  };

  const handleProceed = () => {
    if (!currentAgent) return;

    const questionId = uuid();
    const timestamp = new Date().toISOString();

    const payload: any = {
      agentId: currentAgent.id,
      message: "proceed",
      questionId,
      timestamp,
      agentEndpoint: currentAgent.endpoint,
      sessionId,
    };

    dispatch(sendMessageToAgent(payload));
  };

  const handleNavigateToAssessment = () => {
    navigate("/skill-gap-assessment");
  };

  // Extract skills from response
  const skills: SkillResult[] = assessmentData?.skills || assessmentData?.result?.data?.skills || [];
  const role = assessmentData?.role || assessmentData?.result?.data?.role || "";
  const version = assessmentData?.version || assessmentData?.result?.data?.version || "";

  // Calculate severity counts
  const severityCounts = {
    exceeds_target: skills.filter((s) => s.severity === "exceeds_target").length,
    meets_target: skills.filter((s) => s.severity === "meets_target").length,
    minor_gap: skills.filter((s) => s.severity === "minor_gap").length,
    moderate_gap: skills.filter((s) => s.severity === "moderate_gap").length,
    significant_gap: skills.filter((s) => s.severity === "significant_gap").length,
  };

  const strengths = deriveStrengths(skills);
  const weaknesses = deriveWeaknesses(skills);

  return (
    <Box className={styles.learningPathContainer}>
      {/* Error display */}
      {error && (
        <Alert
          severity="error"
          onClose={() => setError(null)}
          sx={{ mb: 2, mx: 2 }}
        >
          {error}
        </Alert>
      )}

      {/* No data available message */}
      {noDataAvailable && (
        <Box className={styles.learningPathNoDataWrapper}>
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 1.5 }}>
              Please complete the skill gap assessment and return here to
              generate the learning path.
            </Typography>
          </Alert>
          <Box className={styles.learningPathButtonWrapper}>
            <Button
              variant="contained"
              onClick={handleNavigateToAssessment}
              className={styles.learningPathButton}
            >
              Go to Skill Gap Assessment
            </Button>
          </Box>
        </Box>
      )}

      {/* Generate button - shown when no assessment data is loaded */}
      {!assessmentData && !noDataAvailable && (
        <Box className={styles.learningPathButtonWrapper}>
          <Button
            variant="contained"
            onClick={handleGenerateLearningPath}
            disabled={loading}
            className={styles.learningPathButton}
            sx={{
              background: "linear-gradient(135deg, #00a888 0%, #00d4aa 100%)",
              color: "#fff",
              fontSize: "16px",
              fontWeight: 700,
              padding: "14px 48px",
              borderRadius: "50px",
              textTransform: "none",
              boxShadow: "0 6px 20px rgba(0, 168, 136, 0.3)",
              "&:hover": {
                background: "linear-gradient(135deg, #008f75 0%, #00b894 100%)",
              },
            }}
          >
            {loading ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1, color: "#fff" }} />
                Loading Assessment Data...
              </>
            ) : (
              "Generate Learning Path"
            )}
          </Button>
        </Box>
      )}

      {/* Results display - shown after data is fetched */}
      {assessmentData && (
        <Box sx={{ p: 3 }}>
          {/* Header */}
          <Typography
            sx={{
              fontSize: "28px",
              fontWeight: 800,
              color: "#1a1a1a",
              mb: 2,
              textAlign: "center",
            }}
          >
            Ready to turn your assessment results into a personalized learning plan?
          </Typography>

          {/* Assessment Results Section */}
          <Box
            sx={{
              background: "#fff",
              borderRadius: "12px",
              p: 3,
              mb: 3,
              boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
            }}
          >
            <Typography sx={{ fontSize: "24px", fontWeight: 700, mb: 2 }}>
              Assessment Results
            </Typography>

            {/* Role info */}
            {role && (
              <Box sx={{ display: "flex", gap: 1, mb: 3 }}>
                <Chip
                  label={`Role: ${role}`}
                  sx={{ bgcolor: "#00a888", color: "#fff", fontWeight: 600 }}
                />
                {version && (
                  <Chip
                    label={version}
                    sx={{ bgcolor: "#e0f2f1", color: "#00a888", fontWeight: 600 }}
                  />
                )}
              </Box>
            )}

            {/* Severity counts */}
            <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
              {[
                { key: "exceeds_target", label: "EXCEEDS TARGET", color: "#00a888" },
                { key: "meets_target", label: "MEETS TARGET", color: "#0ea5e9" },
                { key: "minor_gap", label: "MINOR GAP", color: "#f59e0b" },
                { key: "moderate_gap", label: "MODERATE GAP", color: "#f97316" },
                { key: "significant_gap", label: "SIGNIFICANT GAP", color: "#ef4444" },
              ].map((item) => (
                <Box
                  key={item.key}
                  sx={{
                    flex: 1,
                    minWidth: "120px",
                    p: 2,
                    border: `2px solid ${item.color}`,
                    borderRadius: "12px",
                    textAlign: "center",
                  }}
                >
                  <Typography sx={{ fontSize: "28px", fontWeight: 800 }}>
                    {severityCounts[item.key as keyof typeof severityCounts]}
                  </Typography>
                  <Typography
                    sx={{ fontSize: "11px", fontWeight: 700, color: "#666", textTransform: "uppercase" }}
                  >
                    {item.label}
                  </Typography>
                </Box>
              ))}
            </Box>

            {/* Key Highlights */}
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 3, mb: 3 }}>
              {/* Strengths */}
              <Box sx={{ p: 3, bgcolor: "#ecfdf5", borderRadius: "12px" }}>
                <Typography sx={{ fontSize: "18px", fontWeight: 700, mb: 1 }}>
                  ✦ Key Strengths
                </Typography>
                <Typography sx={{ fontSize: "13px", color: "#666", mb: 2 }}>
                  Top skills where you meet or exceed the target
                </Typography>
                {strengths.map((skill) => (
                  <Box key={skill.skill} sx={{ mb: 2 }}>
                    <Typography sx={{ fontWeight: 600, mb: 0.5 }}>
                      {skill.skill}
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <Chip
                        label={`${skill.current_score} / ${skill.target_level}`}
                        size="small"
                        sx={{ bgcolor: "#10b981", color: "#fff", fontWeight: 600 }}
                      />
                      <Chip
                        label={skill.severity === "exceeds_target" ? "EXCEEDS" : "MEETS"}
                        size="small"
                        sx={{ bgcolor: "#00a888", color: "#fff", fontWeight: 600 }}
                      />
                    </Box>
                  </Box>
                ))}
              </Box>

              {/* Weaknesses */}
              <Box sx={{ p: 3, bgcolor: "#fef2f2", borderRadius: "12px" }}>
                <Typography sx={{ fontSize: "18px", fontWeight: 700, mb: 1 }}>
                  ▼ Key Weaknesses
                </Typography>
                <Typography sx={{ fontSize: "13px", color: "#666", mb: 2 }}>
                  Skills with the largest gap to the target level
                </Typography>
                {weaknesses.map((skill) => (
                  <Box key={skill.skill} sx={{ mb: 2 }}>
                    <Typography sx={{ fontWeight: 600, mb: 0.5 }}>
                      {skill.skill}
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <Chip
                        label={`${skill.current_score} / ${skill.target_level}`}
                        size="small"
                        sx={{ bgcolor: "#ef4444", color: "#fff", fontWeight: 600 }}
                      />
                      <Chip
                        label={skill.severity.replace(/_/g, " ").toUpperCase()}
                        size="small"
                        sx={{ bgcolor: "#f97316", color: "#fff", fontWeight: 600 }}
                      />
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>

            {/* All Skills List */}
            <Box sx={{ bgcolor: "#f9fafb", borderRadius: "12px", p: 2 }}>
              {skills.map((skill, index) => (
                <Box
                  key={index}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    py: 1.5,
                    borderBottom: index < skills.length - 1 ? "1px solid #e5e7eb" : "none",
                  }}
                >
                  <Typography sx={{ fontWeight: 500 }}>• {skill.skill}</Typography>
                  <Box sx={{ display: "flex", gap: 3 }}>
                    <Typography sx={{ fontWeight: 600, color: "#666" }}>
                      {skill.current_score}
                    </Typography>
                    <Typography sx={{ fontWeight: 600, color: "#666" }}>
                      {skill.target_level}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>

          {/* Proceed button */}
          <Box sx={{ textAlign: "center" }}>
            <Button
              variant="contained"
              onClick={handleProceed}
              disabled={agentLoading}
              sx={{
                background: "linear-gradient(135deg, #00a888 0%, #00d4aa 100%)",
                color: "#fff",
                fontSize: "18px",
                fontWeight: 700,
                padding: "16px 64px",
                borderRadius: "50px",
                textTransform: "none",
                boxShadow: "0 6px 20px rgba(0, 168, 136, 0.3)",
                "&:hover": {
                  background: "linear-gradient(135deg, #008f75 0%, #00b894 100%)",
                },
              }}
            >
              {agentLoading ? (
                <>
                  <CircularProgress
                    size={20}
                    sx={{ mr: 1, color: "#fff" }}
                  />
                  Processing...
                </>
              ) : (
                "Proceed"
              )}
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
}
